"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VendorPaymentProfilesQuery = exports.VendorPaymentProfileUpdateTransport = exports.VendorPaymentProfileCreateTransport = exports.VendorPaymentProfileTransport = exports.GLOBAL_CONFIG_ID = exports.ACHSpeedLabels = exports.ACHSpeed = exports.ACHSpeeds = void 0;
const typebox_1 = require("@sinclair/typebox");
const common_1 = require("../common");
const virtual_credit_card_transport_1 = require("./virtual-credit-card-transport");
/**
 * Vendor Payment Profile Transport Types
 *
 * Used for managing vendor payment preferences, bank account information,
 * payout configuration, and payout schedules in the vendor payout system.
 *
 * This entity combines:
 * - Vendor account details (Plaid tokens, SVB VCN card, bank info)
 * - Payout configuration (net terms, frequency, approval thresholds)
 *
 * The record with id = GLOBAL_CONFIG_ID ('00000000-0000-0000-0000-000000000000')
 * stores system-wide default settings.
 */
// Status enums
const ProfileStatus = typebox_1.Type.Union([typebox_1.Type.Literal('ACTIVE'), typebox_1.Type.Literal('SUSPENDED'), typebox_1.Type.Literal('PENDING_SETUP')]);
// Use common PaymentMethod type to support all payment methods (ACH, VCC, CHECK, WIRE, CASH, etc.)
const PaymentMethod = common_1.PaymentMethod;
const VerificationStatus = typebox_1.Type.Union([typebox_1.Type.Literal('VERIFIED'), typebox_1.Type.Literal('PENDING'), typebox_1.Type.Literal('FAILED')]);
const OnboardingMethod = typebox_1.Type.Union([
    typebox_1.Type.Literal('PLAID_LINK'),
    typebox_1.Type.Literal('PLAID_MIGRATE'),
    typebox_1.Type.Literal('MANUAL_ENTRY'),
]);
// ACH Speed types (for vendor preference override)
exports.ACHSpeeds = ['STANDARD', 'NEXT_DAY', 'SAME_DAY', 'INSTANT'];
exports.ACHSpeed = typebox_1.Type.Union(exports.ACHSpeeds.map((s) => typebox_1.Type.Literal(s)));
exports.ACHSpeedLabels = {
    STANDARD: 'Standard',
    NEXT_DAY: 'Next Day',
    SAME_DAY: 'Same Day',
    INSTANT: 'Instant',
};
// Payout frequency types
const PayoutFrequency = typebox_1.Type.Union([
    typebox_1.Type.Literal('DAILY'),
    typebox_1.Type.Literal('WEEKLY'),
    typebox_1.Type.Literal('BIWEEKLY'),
    typebox_1.Type.Literal('MONTHLY'),
]);
// Weekend handling types
const WeekendHandling = typebox_1.Type.Union([typebox_1.Type.Literal('ROUND_DOWN'), typebox_1.Type.Literal('ROUND_UP')]);
// Global config ID constant
exports.GLOBAL_CONFIG_ID = '00000000-0000-0000-0000-000000000000';
// Main transport schema
exports.VendorPaymentProfileTransport = typebox_1.Type.Object({
    id: typebox_1.Type.String({ format: 'uuid' }),
    vendorID: typebox_1.Type.String({ format: 'uuid' }),
    status: ProfileStatus,
    defaultPaymentMethod: PaymentMethod,
    preferredACHSpeed: typebox_1.Type.Optional(exports.ACHSpeed),
    onboardingMethod: typebox_1.Type.Optional(OnboardingMethod),
    verificationStatus: typebox_1.Type.Optional(VerificationStatus),
    // Security: Never expose actual tokens, only boolean flags
    hasPlaidAccount: typebox_1.Type.Boolean(),
    plaidAccountMask: typebox_1.Type.Optional(typebox_1.Type.String({ description: 'Last 4 digits of bank account (e.g., "1234")' })),
    plaidAccountName: typebox_1.Type.Optional(typebox_1.Type.String({ description: 'Bank name/account name (e.g., "Chase Checking")' })),
    plaidAccountType: typebox_1.Type.Optional(typebox_1.Type.String({ description: 'Account type (e.g., "checking", "savings")' })),
    plaidInstitutionID: typebox_1.Type.Optional(typebox_1.Type.String({ description: 'Plaid institution ID (e.g., "ins_123456") for re-fetching metadata' })),
    plaidInstitutionName: typebox_1.Type.Optional(typebox_1.Type.String({ description: 'Institution display name (e.g., "Bank of America")' })),
    plaidInstitutionLogo: typebox_1.Type.Optional(typebox_1.Type.String({ description: 'Base64-encoded 152x152 PNG institution logo from Plaid' })),
    plaidInstitutionPrimaryColor: typebox_1.Type.Optional(typebox_1.Type.String({ description: 'Institution primary brand color (hex, e.g., "#0052CC")' })),
    hasSVBCard: typebox_1.Type.Boolean(),
    // Computed readiness flags
    isACHReady: typebox_1.Type.Boolean(),
    isVCCReady: typebox_1.Type.Boolean(),
    // VCC ToS acceptance status (computed at the API layer from vcc_tos_acceptance)
    vccTosAccepted: typebox_1.Type.Optional(typebox_1.Type.Boolean()),
    vccTosAcceptedAt: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'date-time' })),
    vccTosAcceptedByEmail: typebox_1.Type.Optional(typebox_1.Type.String()),
    vccTosAcceptedVersion: typebox_1.Type.Optional(typebox_1.Type.Integer()),
    // VCC info (from VirtualCreditCard entity, only present when hasSVBCard is true)
    /** Current card balance in cents — sum of all pending VCC payouts. 0 when no payouts pending. */
    vccBalanceCents: typebox_1.Type.Optional(typebox_1.Type.Integer({ minimum: 0 })),
    /** Card status: DORMANT (no pending payouts), ACTIVE (has pending payouts), TERMINATED */
    vccStatus: typebox_1.Type.Optional(typebox_1.Type.String()),
    /** All of the vendor's virtual cards, newest first — includes previously canceled (TERMINATED) ones */
    vccs: typebox_1.Type.Optional(typebox_1.Type.Array(virtual_credit_card_transport_1.VirtualCreditCardTransport)),
    // Vendor metadata
    vendorBusinessName: typebox_1.Type.Optional(typebox_1.Type.String()),
    vendorContactEmail: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'email' })),
    notes: typebox_1.Type.Optional(typebox_1.Type.String()),
    // Payout status
    payoutStatus: typebox_1.Type.Union([typebox_1.Type.Literal('ACTIVE'), typebox_1.Type.Literal('MANUAL_ONLY'), typebox_1.Type.Literal('FROZEN')]),
    payoutStatusReason: typebox_1.Type.Optional(typebox_1.Type.String()),
    payoutStatusUpdatedAt: typebox_1.Type.Optional(typebox_1.Type.String()),
    payoutStatusUpdatedBy: typebox_1.Type.Optional(typebox_1.Type.String()),
    // Payout configuration fields (previously in PayoutConfiguration)
    payoutFrequency: typebox_1.Type.Optional(PayoutFrequency),
    defaultACHSpeed: typebox_1.Type.Optional(exports.ACHSpeed),
    enabledACHSpeeds: typebox_1.Type.Optional(typebox_1.Type.Array(exports.ACHSpeed)),
    weekendHandling: typebox_1.Type.Optional(WeekendHandling),
    achCutoffHour: typebox_1.Type.Optional(typebox_1.Type.Integer({ minimum: 0, maximum: 23 })),
    autoApprovalThreshold: typebox_1.Type.Optional(typebox_1.Type.Integer({ minimum: 0 })),
    requireDualApproval: typebox_1.Type.Optional(typebox_1.Type.Boolean()),
    enabledPaymentMethods: typebox_1.Type.Optional(typebox_1.Type.Array(PaymentMethod)),
    enableAutomatedPayouts: typebox_1.Type.Optional(typebox_1.Type.Boolean()),
    maxBatchSize: typebox_1.Type.Optional(typebox_1.Type.Integer({ minimum: 1 })),
    // Computed flag for global config
    isGlobal: typebox_1.Type.Optional(typebox_1.Type.Boolean()),
    // Entity metadata
    metadata: typebox_1.Type.Optional(typebox_1.Type.Object({
        createdAt: typebox_1.Type.String(),
        createdByUserID: typebox_1.Type.String(),
        lastUpdatedAt: typebox_1.Type.String(),
        lastUpdatedByUserID: typebox_1.Type.String(),
    })),
});
// Create transport (for POST requests)
// For global config, use vendorID = GLOBAL_CONFIG_ID ('00000000-0000-0000-0000-000000000000')
exports.VendorPaymentProfileCreateTransport = typebox_1.Type.Object({
    vendorID: typebox_1.Type.String({ format: 'uuid' }),
    defaultPaymentMethod: typebox_1.Type.Optional(PaymentMethod),
    preferredACHSpeed: typebox_1.Type.Optional(exports.ACHSpeed),
    vendorBusinessName: typebox_1.Type.Optional(typebox_1.Type.String()),
    vendorContactEmail: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'email' })),
    notes: typebox_1.Type.Optional(typebox_1.Type.String()),
    // Payout configuration fields
    payoutFrequency: typebox_1.Type.Optional(PayoutFrequency),
    defaultACHSpeed: typebox_1.Type.Optional(exports.ACHSpeed),
    enabledACHSpeeds: typebox_1.Type.Optional(typebox_1.Type.Array(exports.ACHSpeed)),
    weekendHandling: typebox_1.Type.Optional(WeekendHandling),
    achCutoffHour: typebox_1.Type.Optional(typebox_1.Type.Integer({ minimum: 0, maximum: 23 })),
    autoApprovalThreshold: typebox_1.Type.Optional(typebox_1.Type.Integer({ minimum: 0 })),
    requireDualApproval: typebox_1.Type.Optional(typebox_1.Type.Boolean()),
    enabledPaymentMethods: typebox_1.Type.Optional(typebox_1.Type.Array(PaymentMethod)),
    enableAutomatedPayouts: typebox_1.Type.Optional(typebox_1.Type.Boolean()),
    maxBatchSize: typebox_1.Type.Optional(typebox_1.Type.Integer({ minimum: 1 })),
});
// Payout status (for freezing/unfreezing vendors)
const VendorPayoutStatus = typebox_1.Type.Union([typebox_1.Type.Literal('ACTIVE'), typebox_1.Type.Literal('MANUAL_ONLY'), typebox_1.Type.Literal('FROZEN')]);
// Update transport (for PATCH requests)
exports.VendorPaymentProfileUpdateTransport = typebox_1.Type.Object({
    defaultPaymentMethod: typebox_1.Type.Optional(PaymentMethod),
    preferredACHSpeed: typebox_1.Type.Optional(exports.ACHSpeed),
    vendorBusinessName: typebox_1.Type.Optional(typebox_1.Type.String()),
    vendorContactEmail: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'email' })),
    notes: typebox_1.Type.Optional(typebox_1.Type.String()),
    // Payout status (ACTIVE, MANUAL_ONLY, FROZEN)
    payoutStatus: typebox_1.Type.Optional(VendorPayoutStatus),
    payoutStatusReason: typebox_1.Type.Optional(typebox_1.Type.String()),
    // Payout configuration fields
    payoutFrequency: typebox_1.Type.Optional(PayoutFrequency),
    defaultACHSpeed: typebox_1.Type.Optional(exports.ACHSpeed),
    enabledACHSpeeds: typebox_1.Type.Optional(typebox_1.Type.Array(exports.ACHSpeed)),
    weekendHandling: typebox_1.Type.Optional(WeekendHandling),
    achCutoffHour: typebox_1.Type.Optional(typebox_1.Type.Integer({ minimum: 0, maximum: 23 })),
    autoApprovalThreshold: typebox_1.Type.Optional(typebox_1.Type.Integer({ minimum: 0 })),
    requireDualApproval: typebox_1.Type.Optional(typebox_1.Type.Boolean()),
    enabledPaymentMethods: typebox_1.Type.Optional(typebox_1.Type.Array(PaymentMethod)),
    enableAutomatedPayouts: typebox_1.Type.Optional(typebox_1.Type.Boolean()),
    maxBatchSize: typebox_1.Type.Optional(typebox_1.Type.Integer({ minimum: 1 })),
});
// Query parameters for listing profiles
exports.VendorPaymentProfilesQuery = typebox_1.Type.Object({
    limit: typebox_1.Type.Optional(typebox_1.Type.Integer({ minimum: 1, maximum: 100 })),
    offset: typebox_1.Type.Optional(typebox_1.Type.Integer({ minimum: 0 })),
    status: typebox_1.Type.Optional(ProfileStatus),
    paymentMethod: typebox_1.Type.Optional(PaymentMethod),
    verificationStatus: typebox_1.Type.Optional(VerificationStatus),
});
//# sourceMappingURL=vendor-payment-profile-transport.js.map